// src/components/VaccineSummary.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import DataTable from "../AppDataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

const customExpandStyles = {
    header: {
      style: {
        backgroundColor: "#6BA9B4",
        color: "white",
        fontSize: "16px",
        fontWeight: "bold",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#6BA9B4",
        color: "white",
        fontSize: "14px",
        fontWeight: "semibold",
        padding: "10px",
      },
    },
    cells: {
      style: {
        padding: "10px",
        fontSize: "14px",
      },
    },
    rows: {
      style: {
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
      },
    },
  };
const VaccineSummary = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();
  const [vaccineSummary, setVaccineSummary] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login");
    } else {
      generateMockData();
    }
  }, [sessionId, navigate]);

  const generateMockData = () => {
    // ข้อมูลสมมติเพิ่มเติม
    const mockVaccines = [
      { VaccineID: "VAC001", VaccineNameTH: "วัคซีนป้องกันโรคระบาด A", PackageSize: 10, PriceDistribution: "350" },
      { VaccineID: "VAC002", VaccineNameTH: "วัคซีนป้องกันโรคระบาด B", PackageSize: 5, PriceDistribution: "500" },
      { VaccineID: "VAC003", VaccineNameTH: "วัคซีนป้องกันโรคระบาด C", PackageSize: 20, PriceDistribution: "200" },
      { VaccineID: "VAC004", VaccineNameTH: "วัคซีนป้องกันโรคระบาด D", PackageSize: 8, PriceDistribution: "400" },
    ];

    const mockWarehouses = [
      { WarehouseID: "DCONTROL", WarehouseName: "สำนักควบคุม ป้องกัน และบำบัดโรคสัตว์" },
      { WarehouseID: "DLDBVB", WarehouseName: "สำนักเทคโนโลยีชีวภัณฑ์สัตว์" },
      { WarehouseID: "PVLO001", WarehouseName: "สำนักงานปศุสัตว์จังหวัดพระนครศรีอยุธยา" },
      { WarehouseID: "PVLO002", WarehouseName: "สำนักงานปศุสัตว์จังหวัดนนทบุรี" },
    ];

    const mockReservations = [
      // การจองจากเกษตรกร
      { ReservedBy: "farmer1@example.com", VaccineDetails: [{ VaccineID: "VAC001", RequestQuantity: 50 }, { VaccineID: "VAC002", RequestQuantity: 30 }, { VaccineID: "VAC003", RequestQuantity: 100 }] },
      { ReservedBy: "farmer2@example.com", VaccineDetails: [{ VaccineID: "VAC001", RequestQuantity: 20 }, { VaccineID: "VAC004", RequestQuantity: 40 }] },
      { ReservedBy: "farmer3@example.com", VaccineDetails: [{ VaccineID: "VAC002", RequestQuantity: 25 }, { VaccineID: "VAC003", RequestQuantity: 50 }] },
      // การจองจากหน่วยงาน
      { OfficeID: "DCONTROL", VaccineDetails: [{ VaccineID: "VAC001", RequestQuantity: 100 }, { VaccineID: "VAC002", RequestQuantity: 40 }, { VaccineID: "VAC004", RequestQuantity: 80 }] },
      { OfficeID: "DLDBVB", VaccineDetails: [{ VaccineID: "VAC002", RequestQuantity: 60 }, { VaccineID: "VAC003", RequestQuantity: 120 }] },
      { OfficeID: "PVLO001", VaccineDetails: [{ VaccineID: "VAC001", RequestQuantity: 150 }, { VaccineID: "VAC003", RequestQuantity: 80 }] },
      { OfficeID: "PVLO002", VaccineDetails: [{ VaccineID: "VAC002", RequestQuantity: 30 }, { VaccineID: "VAC004", RequestQuantity: 50 }] },
    ];

    const summary = mockVaccines.map((vaccine) => {
      const packageSize = vaccine.PackageSize || 1;
      const unitPrice = parseFloat(vaccine.PriceDistribution || "350");

      // ยอดจากเกษตรกร
      const farmerReservations = mockReservations.filter((r) => r.ReservedBy && !r.OfficeID);
      const farmerDetails = farmerReservations.flatMap((r) => r.VaccineDetails || []);
      const farmerVaccine = farmerDetails.filter((d) => d.VaccineID === vaccine.VaccineID);
      const farmerQuantity = farmerVaccine.reduce((sum, d) => sum + (d.RequestQuantity || 0), 0);
      const farmerDoses = Math.ceil(farmerQuantity / packageSize);
      const farmerTotalPrice = farmerQuantity * unitPrice;

      // ยอดจากหน่วยงาน
      const officeReservations = mockReservations.filter((r) => r.OfficeID);
      const officeSummaries = mockWarehouses.map((warehouse) => {
        const warehouseReservations = officeReservations.filter((r) => r.OfficeID === warehouse.WarehouseID);
        const warehouseDetails = warehouseReservations.flatMap((r) => r.VaccineDetails || []);
        const warehouseVaccine = warehouseDetails.filter((d) => d.VaccineID === vaccine.VaccineID);
        const quantity = warehouseVaccine.reduce((sum, d) => sum + (d.RequestQuantity || 0), 0);
        const doses = Math.ceil(quantity / packageSize);
        const totalPrice = quantity * unitPrice;
        return {
          warehouseName: warehouse.WarehouseName,
          quantity,
          doses,
          unitPrice,
          totalPrice,
        };
      });

      // ยอดรวมทั้งหมด
      const totalQuantity = farmerQuantity + officeSummaries.reduce((sum, o) => sum + o.quantity, 0);
      const totalDoses = Math.ceil(totalQuantity / packageSize);
      const totalPrice = totalQuantity * unitPrice;

      return {
        vaccineName: vaccine.VaccineNameTH,
        totalQuantity,
        totalDoses,
        unitPrice,
        totalPrice,
        farmer: {
          quantity: farmerQuantity,
          doses: farmerDoses,
          unitPrice,
          totalPrice: farmerTotalPrice,
        },
        offices: officeSummaries,
      };
    });

    setVaccineSummary(summary);
  };

  const columns = [
    {
      name: "ชื่อวัคซีน",
      selector: (row) => row.vaccineName,
      sortable: true,
      width: "250px",
    },
    {
      name: "ยอดจองรวม (ขวด)",
      selector: (row) => row.totalQuantity,
      sortable: true,
      format: (row) => row.totalQuantity.toLocaleString(),
      right: true,
    },
    {
      name: "จำนวนโดสรวม",
      selector: (row) => row.totalDoses,
      sortable: true,
      format: (row) => row.totalDoses.toLocaleString(),
      right: true,
    },
    {
      name: "ราคาต่อหน่วย (บาท)",
      selector: (row) => row.unitPrice,
      sortable: true,
      format: (row) => row.unitPrice.toLocaleString(),
      right: true,
    },
    {
      name: "ราคารวม (บาท)",
      selector: (row) => row.totalPrice,
      sortable: true,
      format: (row) => row.totalPrice.toLocaleString(),
      right: true,
    },
  ];

  const ExpandedComponent = ({ data }) => {
    const expandedData = [
      {
        type: "เกษตรกร",
        name: "เกษตรกร",
        quantity: data.farmer.quantity,
        doses: data.farmer.doses,
        unitPrice: data.farmer.unitPrice,
        totalPrice: data.farmer.totalPrice,
        isFarmer: true,
      },
      ...data.offices.map((office) => ({
        type: "หน่วยงาน",
        name: office.warehouseName,
        quantity: office.quantity,
        doses: office.doses,
        unitPrice: office.unitPrice,
        totalPrice: office.totalPrice,
        isFarmer: false,
      })),
    ];

    return (
      <Box p={25} bg="gray.50">
        <DataTable
          columns={[
            { name: "ประเภท", selector: (row) => row.type, width: "150px" },
            { name: "ชื่อ", selector: (row) => row.name, width: "250px" },
            { name: "ยอดจอง (ขวด)", selector: (row) => row.quantity.toLocaleString(), right: true },
            { name: "จำนวนโดส", selector: (row) => row.doses.toLocaleString(), right: true },
            { name: "ราคาต่อหน่วย (บาท)", selector: (row) => row.unitPrice.toLocaleString(), right: true },
            { name: "ราคารวม (บาท)", selector: (row) => row.totalPrice.toLocaleString(), right: true },
          ]}
          data={expandedData}
          noHeader
          customStyles={{
            ...customExpandStyles,
            rows: {
              style: (row) => ({
                backgroundColor: row.isFarmer ? "#e6ffe6" : "inherit", // สีเขียวอ่อนสำหรับเกษตรกร
              }),
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        ยอดรวมการสั่งจองวัคซีน
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
        <Box mb={5} borderRadius="full" w="100%" bgColor="iOrange.500" color="white" p={2}>
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faTable} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              สรุปยอดการสั่งจองวัคซีน
            </Heading>
          </HStack>
        </Box>

        <DataTable
          columns={columns}
          data={vaccineSummary}
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          expandableRowExpanded={(row) => expandedRows.includes(row.vaccineName)}
          onRowExpandToggled={(isExpanded, row) => {
            setExpandedRows((prev) =>
              isExpanded ? [...prev, row.vaccineName] : prev.filter((name) => name !== row.vaccineName)
            );
          }}
          pagination
          highlightOnHover
          striped
          customStyles={customStyles}
        />
      </Box>
    </Box>
  );
};

export default VaccineSummary;